import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useApp } from "@/store/useApp";
import { Logo } from "@/components/Logo";
import { BuildInfo } from "@/components/BuildInfo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, Lock, ArrowRight, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { getApiBaseUrl, getApiErrorMessage } from "@/lib/api";
import { staffEntryRoute } from "@/lib/staffWelcome";
import { isStaffRole } from "@/lib/types";
import { loginAccount, toAccount } from "@/lib/authApi";

const OAUTH_ERROR_MESSAGES: Record<string, string> = {
  email_not_verified: "Seu e-mail do Google não está verificado. Verifique no Google e tente novamente.",
  invalid_user_info: "O Google não retornou as informações necessárias para o login.",
  oauth_error: "Não foi possível concluir o login com Google.",
};

export default function Login() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const account = useApp((s) => s.account);
  const authStatus = useApp((s) => s.authStatus);
  const login = useApp((s) => s.login);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const errorCode = searchParams.get("error");
    if (!errorCode) return;
    toast.error(OAUTH_ERROR_MESSAGES[errorCode] ?? OAUTH_ERROR_MESSAGES.oauth_error);
    searchParams.delete("error");
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (authStatus !== "ready" || !account) return;
    navigate(staffEntryRoute(account), { replace: true });
  }, [account, authStatus, navigate]);

  const loginWithGoogle = () => {
    window.location.href = `${getApiBaseUrl()}/oauth2/authorization/google`;
  };

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // Lê direto do formulário em vez de confiar só no estado do React: autofill/gerenciadores
    // de senha (comum no celular) preenchem o campo sem disparar onChange, deixando o estado
    // vazio e fazendo o login falhar como "senha vazia" mesmo com o campo preenchido.
    const form = e.currentTarget;
    const emailValue = ((form.elements.namedItem("email") as HTMLInputElement | null)?.value ?? email)
      .trim()
      .toLowerCase();
    const passwordValue = (form.elements.namedItem("password") as HTMLInputElement | null)?.value ?? password;

    if (!emailValue || !passwordValue) {
      toast.error("Preencha email e senha");
      return;
    }
    setIsSubmitting(true);

    try {
      const response = await loginAccount({
        email: emailValue,
        password: passwordValue,
      });
      const account = toAccount(response);
      login(account);
      // Conta autenticada mas marcada pelo admin para trocar a senha: a identidade já foi provada no
      // login; levamos o usuário para a troca autenticada antes de seguir.
      if (account.passwordResetRequired) {
        navigate("/redefinir-senha", { replace: true });
        return;
      }
      // Funcionário de back-office é saudado pelo Nexo na tela de boas-vindas (pelo nome) — sem toast de
      // boas-vindas (evita um aviso genérico cobrindo o mascote). Cliente/entregador seguem com o toast.
      const isBackOffice = isStaffRole(account.role) && account.role !== "ENTREGADOR";
      if (!isBackOffice) toast.success("Bem-vindo de volta");
      navigate(staffEntryRoute(account), { replace: true });
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Nao foi possivel entrar"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <div className="flex-1 grid lg:grid-cols-2">
      {/* Lado visual */}
      <div className="hidden lg:flex relative hero-gradient overflow-hidden flex-col justify-between p-12 text-primary-foreground">
        <Logo className="text-primary-foreground [&>span:last-child]:text-primary-foreground" />
        <div className="relative z-10 max-w-md space-y-6">
          <div className="inline-flex items-center gap-2 rounded-full border border-primary-foreground/20 bg-primary-foreground/10 backdrop-blur-sm px-3 py-1.5 text-xs font-medium">
            <Sparkles className="h-3.5 w-3.5" />
            Pedidos B2B de hortifrúti
          </div>
          <h1 className="font-display text-5xl font-bold leading-[1.05] tracking-tight">
            O catálogo que organiza seu pedido recorrente.
          </h1>
          <p className="text-primary-foreground/80 text-lg leading-relaxed">
            Restaurantes, supermercados e cozinhas industriais montam pedidos com a configuração exata de corte, unidade e janela de entrega.
          </p>
        </div>
        <div className="relative z-10 grid grid-cols-3 gap-4 max-w-lg text-sm">
          {[
            { k: "+130", v: "produtos no catálogo" },
            { k: "B2B", v: "preços por cliente" },
            { k: "24h", v: "ciclo de pedido" },
          ].map((s) => (
            <div key={s.k} className="border-l-2 border-primary-foreground/30 pl-3">
              <div className="font-display text-2xl font-bold">{s.k}</div>
              <div className="text-primary-foreground/70 text-xs">{s.v}</div>
            </div>
          ))}
        </div>
        <div className="absolute -right-32 -top-32 h-96 w-96 rounded-full bg-primary-foreground/5 blur-3xl" />
        <div className="absolute -right-20 bottom-10 h-72 w-72 rounded-full bg-primary-foreground/5 blur-3xl" />
      </div>

      {/* Lado form */}
      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-sm space-y-8">
          <div className="lg:hidden">
            <Logo />
          </div>
          <div className="space-y-2">
            <h2 className="font-display text-3xl font-bold tracking-tight">Entrar</h2>
            <p className="text-sm text-muted-foreground">
              Acesse sua conta Naturexpress para montar seus pedidos.
            </p>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  name="email"
                  type="text"
                  autoComplete="username"
                  placeholder="voce@empresa.com.br"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-9 h-11"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Senha</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-9 h-11"
                />
              </div>
              <div className="flex justify-end">
                <Link to="/recuperar-acesso" className="text-xs text-muted-foreground hover:text-foreground hover:underline">
                  Esqueci minha senha
                </Link>
              </div>
            </div>

            <Button type="submit" variant="hero" size="lg" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? "Entrando..." : "Entrar"}
              {!isSubmitting && <ArrowRight className="h-4 w-4" />}
            </Button>
          </form>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">ou</span>
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            size="lg"
            className="w-full"
            onClick={loginWithGoogle}
            disabled={isSubmitting}
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
              <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                fill="#4285F4"
              />
              <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.26 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                fill="#34A853"
              />
              <path
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                fill="#FBBC05"
              />
              <path
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                fill="#EA4335"
              />
            </svg>
            Entrar com Google
          </Button>

          <div className="text-sm text-muted-foreground text-center">
            Ainda não tem conta?{" "}
            <Link to="/signup" className="font-semibold text-primary hover:underline">
              Criar conta
            </Link>
          </div>

          <p className="text-xs text-muted-foreground text-center">
            Ao continuar, você concorda com nossos{" "}
            <Link to="/terms" className="underline hover:text-foreground">
              Termos de Uso
            </Link>{" "}
            e{" "}
            <Link to="/privacy" className="underline hover:text-foreground">
              Política de Privacidade
            </Link>
            .
          </p>
        </div>
      </div>
      </div>
      <footer className="py-4 px-6">
        <BuildInfo />
      </footer>
    </div>
  );
}
